import { Component } from 'san';
import * as _ from 'lodash';

import { IOrganization, IProject, IUser } from 'src/models';
import { MAX_PROJECT_ID_LEVEL } from 'src/consts';

import ChangeProjectsList from '../ChangeProjectsList/ChangeProjectsList';
import Project from '../Project/Project';

import auditService from 'src/services/audit.service';
import uiService from 'src/services/ui.service';
import toasterService from 'src/services/toaster.service';
import dialogService from 'src/services/dialog.service';
import projectService from 'src/services/project.service';
import checkOrganizationService from 'src/services/checkOrganization.service';
import diffService from 'src/services/diff.service';
import userService from 'src/services/user.service';
import uploaderService from 'src/services/uploader.service';
import checkChangeProjectsService from 'src/services/checkChangeProjects.service';
import BatchUpload from '../BatchUpload/BatchUpload';


export default class ChangeProjectsBlock extends Component {
  static components = {
    'ui-change-projects-list': ChangeProjectsList,
  };

  static computed = {
    checkedProjects() {
      const projects = this.data.get('projects');
      return _.filter(projects, { _is_checked: true });
    },
    // 发生变化的项目（变更、追加）
    dirtyProjects() {
      const originalProjects = this.data.get('originalProjects');
      const projects = this.data.get('projects');
      const dirtyProjects = diffService.getDirtyProjectsSync(originalProjects, projects);
      return dirtyProjects;
    },
    // 项目列表的puids（包括未变化、变更的、追加的）
    cachedProjectPuids() {
      const projects = this.data.get('projects');
      return _.map(projects, 'puid');
    },
  };

  projectComponent;
  checkChangeProjectsComponent;
  batchUploadComponent;

  constructor(options: any) {
    super(options);
  }

  initData(): {
    auditId: string; // {{}}
    puids: string[];  // {{}} 选择变更的项目的puids
    // 原始项目数据、实时项目数据 对比得出变更的项目数据
    originalProjects: IProject[]; // 原始项目数据（选择的项目）
    projects: IProject[]; // 实时项目数据（变更的项目、追加的项目）
    projectsSnapshot: IProject[]; // 实时项目数据快照（进入组件时保存快照，用来和 projects 对比，判断是否 dirty）
    currentUser: IUser;
    projectToUploadSub: IProject; // 要导入子项目的那个项目

    showingModalCallback: (isShowingModal: boolean) => void; // 显示Project、CheckChangeProjects组件时的回调
    updatedCallback: (projectGroup: { // 项目列表数据变化（导入、修改、追加，不含删除）的回调
      updatedProjects: IProject[];  // 本次变化的项目
      allDirtyProjects: IProject[]; // 所有变化的项目
    }) => void;
    batchUploadCallback: (batchUploadEle: any) => void; // 批量导入的回调
    previewDiffCallback: () => void; // 变更预览
    isSecretProjectEnabled: boolean;
  } {
    return {
      auditId: undefined,
      puids: [],
      originalProjects: [],
      projects: [],
      projectsSnapshot: [],
      currentUser: undefined,
      projectToUploadSub: undefined,
      showingModalCallback: null,
      updatedCallback: null,
      batchUploadCallback: null,
      previewDiffCallback: null,
      isSecretProjectEnabled: false,
    };
  }

  inited() {
    this.initOriginalProjectsAndProjects(this.data.get('auditId'), this.data.get('puids'));
  }

  attached() {
    userService.getCurrentUser().then((currentUser) => {
      this.data.set('currentUser', currentUser);
    });
  }

  disposed() {
    if (this.projectComponent) {
      this.projectComponent.dispose();
    }
    this.projectComponent = null;
    if (this.checkChangeProjectsComponent) {
      this.checkChangeProjectsComponent.dispose();
    }
    this.checkChangeProjectsComponent = null;
    if (this.batchUploadComponent) {
      this.batchUploadComponent.dispose();
    }
    this.batchUploadComponent = null;
  }

  //#region owner调用
  //  获取数据
  getDataSync(): {
    projects: IProject[];
    cached_project_puids: string[];
  } {
    let dirtyProjects = uiService.zipAuditProjectsIsNeedReceiptSync(
      this.data.get('dirtyProjects')
    );
    dirtyProjects = uiService.zipAuditProjectsIsSecretSync(dirtyProjects);
    dirtyProjects = uiService.zipAuditProjectsEndOnSync(dirtyProjects);
    return {
      projects: dirtyProjects,
      cached_project_puids: this.data.get('cachedProjectPuids'),
    };
  }
  // 数据校验
  verifySync(): boolean {
    const dirtyProjects = this.data.get('dirtyProjects');
    return projectService.verifyProjectsSync({
      scene: 'project-change',
      projects: dirtyProjects,
    });
  }
  // 是否变更过
  getIsDirtySync(): boolean {
    const projects = this.data.get('projects');
    const projectsSnapshot = this.data.get('projectsSnapshot');
    return diffService.getIsProjectsDirty(projects, projectsSnapshot);
  }
  // 重置是否变更过
  resetIsDirtySync() {
    let dataKey = 'projects';
    const projects = this.data.get(dataKey);
    dataKey = 'projectsSnapshot';
    const projectsSnapshot = this.data.get(dataKey);
    this.data.splice(dataKey, [0, projectsSnapshot.length, ...projects]);
  }
  // 更新auditId，并更新文件上传server地址
  updateAuditIdSync(auditId: string) {
    this.data.set('auditId', auditId);
    const currentUserId = this.data.get('currentUser').user_id;
    const projectToUploadSub = this.data.get('projectToUploadSub');
    let serverUrl = `/api/files/import-projects?audit_id=${auditId}&user_id=${currentUserId}`;
    if (projectToUploadSub) {
      serverUrl += `&puid=${projectToUploadSub.puid}`;
    }
    uploaderService.updateServerUrl('#batchUpload', serverUrl); // 更新文件上传server地址
  }
  //#endregion

  uploadSuccessCallback(projects: IProject[]) {
    const oldProjects: IProject[] = this.data.get('projects');
    _.forEach(projects, (project) => {
      project._attachments = [];
      project.actions = {
        is_can_change: true,
        is_can_add: true,
      };
    });
    // projects = projectService.unzipProjectsDelayEndOn(projects);
    projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
    projects = uiService.unzipAuditProjectsIsSecretSync(projects);
    projects = uiService.unzipAuditProjectsEndOnSync(projects);
    projects = uiService.assignAuditProjectsSnSync([...oldProjects, ...projects]);
    this.data.splice('projects', [0, oldProjects.length, ...projects]);
    toasterService.show({ type: 'success', title: '导入成功' });
    this.callbackUpdated(projects);
  }

  // 获取原始项目列表、实时项目列表
  initOriginalProjectsAndProjects(auditId: string, puids: string[]) {
    if (auditId) {
      auditService.getChangeAuditOriginalProjectsAndProjects(auditId).then((result) => {
        // 更新原始项目列表
        let dataKey = 'originalProjects';
        const oldOriginalProjects = this.data.get(dataKey);
        this.data.splice(dataKey, [0, oldOriginalProjects.length, ...result.originalProjects]);

        // 更新实时项目列表
        dataKey = 'projects';
        const oldProjects = this.data.get(dataKey);
        this.data.splice(dataKey, [0, oldProjects.length, ...result.projects]);

        // 记录实时项目列表快照
        dataKey = 'projectsSnapshot';
        const projectsSnapshot = this.data.get(dataKey);
        this.data.splice(dataKey, [0, projectsSnapshot.length, ...result.projects]);
      });
    } else {
      auditService.getProjectChangeNewProjects(puids).then(projects => {
        this.checkChangeProjects(projects);
      });
    }
  }

  //#region 选择要变更的项目
  toCheckChangeProjects() {
    checkChangeProjectsService.show({
      // 选中项目的回调方法
      callback: (checkedProjects: IProject[]) => {
        this.checkChangeProjects(checkedProjects);
      },
      //  选择变更项目组件（CheckChangeProjects）的 show 状态发生变化，调用callback
      showCallback: (isShowingCheckChangeProjectsComponent: boolean) => {
        const showingModalCallback = this.data.get('showingModalCallback');
        if (showingModalCallback) {
          showingModalCallback(isShowingCheckChangeProjectsComponent);
        }
      },
    });
  }
  checkChangeProjects(checkedProjects: IProject[]) {
    if (!checkedProjects.length) {
      return;
    }

    _.forEach(checkedProjects, (project) => {
      project._is_checked = false;
    });

    let dataKey = 'originalProjects';
    const oldOriginalProjects = this.data.get(dataKey);
    const oldOriginalProjectIds = _.map(oldOriginalProjects, 'id');
    // 过滤掉已经存在的项目
    const newOriginalProjects = _.reject(checkedProjects, (project) => {
      return _.includes(oldOriginalProjectIds, project.id);
    });
    let originalProjects = [...oldOriginalProjects, ...newOriginalProjects];
    // 分配sn
    originalProjects = uiService.assignAuditProjectsSnSync(originalProjects);
    this.data.splice(dataKey, [0, oldOriginalProjects.length, ...originalProjects]);

    dataKey = 'projects';
    const oldProjects = this.data.get(dataKey);
    const oldProjectIds = _.map(oldProjects, 'id');
    // 过滤掉已经存在的项目
    const newProjects = _.reject(checkedProjects, (project) => {
      return _.includes(oldProjectIds, project.id);
    });
    let projects = [...oldProjects, ...newProjects];
    // 分配sn
    projects = uiService.assignAuditProjectsSnSync(projects);
    this.data.splice(dataKey, [0, oldProjects.length, ...projects]);

    // 记录实时项目列表快照
    dataKey = 'projectsSnapshot';
    const projectsSnapshot = this.data.get(dataKey);
    this.data.splice(dataKey, [0, projectsSnapshot.length, ...projects]);
  }
  //#endregion

  // 手动创建Project组件
  private manualCreateProjectComponent() {
    if (!this.projectComponent) {
      this.projectComponent = new Project({
        data: {},
        owner: this,
        source: '<x-project auditId="{{auditId}}" dirtyProjects="{{dirtyProjects}}" isSecretProjectEnabled="{{isSecretProjectEnabled}}" projectAttributesScene="project-change" on-create-project="createProject($event)" on-update-project="updateProject($event)" on-show-change="onShowEditingProjectChange($event)"></x-project>',
      });
      this.projectComponent.attach(document.getElementById('app'));
    }
  }
  //#region 创建项目
  toCreateProject(parentProject?: IProject) {
    this.manualCreateProjectComponent();
    this.projectComponent.create(parentProject);
  }

  toCreateChildProject(parentProject: IProject) {
    this.verifyCreateChildProject(parentProject).then((isValid) => {
      if (!isValid) {
        return;
      }
      this.toCreateProject(parentProject);
    });
  }

  createProject(project: IProject) {
    const dataKey = 'projects';
    const oldProjects = this.data.get(dataKey);
    let projects = [...oldProjects, project];
    // 分配sn
    projects = uiService.assignAuditProjectsSnSync(projects);

    this.data.splice(dataKey, [0, oldProjects.length, ...projects]);

    this.callbackUpdated([project]);
  }

  private verifyCreateChildProject(parentProject: IProject): Promise<boolean> {
    if (parentProject.id_level >= MAX_PROJECT_ID_LEVEL) {
      toasterService.show({ type: 'warning', title: '最多只能创建5层项目' });
      return Promise.resolve(false);
    }
    // if (4 === parentProject.id_level) {
    //   return permissionService
    //     .getCurrentCertainPermissionOptionValue('允许创建第5层项目')
    //     .then((hasPermission) => {
    //       if (!hasPermission) {
    //         toasterService.show({
    //           type: 'warning',
    //           title: '您没有创建第5层项目的权限',
    //         });
    //       }
    //       return hasPermission;
    //     });
    // }
    return Promise.resolve(true);
  }
  //#endregion

  // 更新全选状态
  toggleCheckAll(isCheckedAll: boolean) {
    const projects: IProject[] = this.data.get('projects');
    _.forEach(projects, project => {
      project._is_checked = isCheckedAll;
    });
    this.data.splice('projects', [0, projects.length, ...projects]);
  }

  //#region 更新项目
  toUpdateProject(project: IProject) {
    this.manualCreateProjectComponent();
    const projects = this.data.get('projects');
    const parentProject = _.find(projects, { puid: project.parent_puid });
    this.projectComponent.update(project, parentProject);
  }

  updateProject(project: IProject) {
    const projects = this.data.get('projects');
    const index = _.findIndex(projects, { puid: project.puid });
    if (-1 === index) {
      return;
    }
    const dataKey = `projects[${index}]`;
    this.data.set(dataKey, project);

    this.callbackUpdated([project]);
  }
  //#endregion

  //#region 删除项目
  // 批量删除项目(删除选中的项目及其子项目)
  toDeleteProjects() {
    const checkedProjects: IProject[] = this.data.get('checkedProjects');
    if (!checkedProjects.length) {
      toasterService.show({ type: 'warning', title: '请选择要移除的项目' });
      return;
    }
    dialogService.show({
      title: '提示',
      content: '您确定要移除您选中的这些项目吗？',
      confirmCallback: () => {
        this.deleteProjects(checkedProjects);
      },
    });
  }
  // 删除项目
  toDeleteProject(project: IProject) {
    const oldProjects: IProject[] = this.data.get('projects');
    const hasChildren = _.some(oldProjects, oldProject => {
      return _.startsWith(oldProject._sn, `${project._sn}.`);
    });
    const content = hasChildren
      ? `您确定要移除『 ${project.title} 』及其子项目吗？`
      : `您确定要移除『 ${project.title} 』项目吗？`;
    dialogService.show({
      title: '提示',
      content,
      confirmCallback: () => {
        this.deleteProjects([project]);
      },
    });
  }
  deleteProjects(checkedProjects: IProject[]) {
    // 删除项目及其子项目
    const checkedProjectsSns = _.map(checkedProjects, '_sn');
    const oldProjects: IProject[] = this.data.get('projects');
    let projects = _.reject(oldProjects, (project) => {
      const result = _.some(checkedProjectsSns, (sn) => {
        return project._sn === sn || _.startsWith(project._sn, `${sn}.`);
      });
      return result;
    });
    projects = uiService.assignAuditProjectsSnSync(projects);
    this.data.splice('projects', [0, oldProjects.length, ...projects]);

    // originalProjects也对应删除相应项目
    const restProjectPuids = _.map(projects, 'puid');
    const dataKey = 'originalProjects';
    const oldOriginalProjects = this.data.get(dataKey);
    const originalProjects = _.filter(oldOriginalProjects, (project) => {
      return _.includes(restProjectPuids, project.puid);
    });
    this.data.splice(dataKey, [0, oldOriginalProjects.length, ...originalProjects]);

    this.callbackUpdated([]);
  }
  //#endregion

  //#region 导入子项目
  toImportSubProjects(project: IProject) {
    this.data.set('projectToUploadSub', project);
    // this.updateAuditIdSync(this.data.get('auditId'));
    this.batchUpload();
  }
  //#endregion

  //#region
  // 设置责任单位
  toSetAccountabilityUnit() {
    const checkedProjects: IProject[] = this.data.get('checkedProjects');
    if (!checkedProjects.length) {
      toasterService.show({ type: 'warning', title: '请选择项目' });
      return;
    }
    checkOrganizationService.show({
      callback: (organization: IOrganization) => {
        this.setAccountabilityUnit(organization);
      },
    });
  }
  setAccountabilityUnit(organization: IOrganization) {
    const projects: IProject[] = this.data.get('projects');
    const checkedProjects: IProject[] = this.data.get('checkedProjects');
    _.forEach(checkedProjects, (p) => {
      const index = _.findIndex(projects, { puid: p.puid });
      this.data.set(`projects[${index}].accountability_unit_id`, organization.org_number);
      this.data.set(`projects[${index}].accountability_unit`, organization.org_name);
      this.data.set(
        `projects[${index}].accountability_unit_full_name`,
        organization.org_full_path
      );
    });
  }
  //#endregion

  //#region 批量导入
  batchUpload() {
    this.manualCreateBatchUploadComponent();
    this.batchUploadComponent.show();
  }
  //#endregion

  // 手动创建BatchUpload组件
  private manualCreateBatchUploadComponent() {
    if (!this.batchUploadComponent) {
      this.batchUploadComponent = new BatchUpload({
        data: {},
        owner: this,
        source: '<x-batch-upload auditId="{{auditId}}" batchUploadCallback="{{batchUploadCallback}}" projectToUploadSub="{{projectToUploadSub}}" on-show-change="onShowEditingProjectChange($event)" on-upload-success="uploadSuccessCallback($event)"></x-batch-upload>'
      });
      this.batchUploadComponent.attach(document.getElementById('app'));
    }
  }

  //#region
  //  编辑项目组件（Project）的 show 状态发生变化，调用callback
  onShowEditingProjectChange(isShowingProjectComponent: boolean) {
    const showingModalCallback = this.data.get('showingModalCallback');
    if (showingModalCallback) {
      showingModalCallback(isShowingProjectComponent);
    }
  }

  // 项目列表数据变化（导入、修改、追加、删除），调用callback
  callbackUpdated(updatedProjects: IProject[]) {
    const updatedCallback = this.data.get('updatedCallback');
    if (updatedCallback) {
      updatedCallback({
        updatedProjects,
        allDirtyProjects: this.data.get('dirtyProjects')
      });
    }
  }
  //#endregion

  // 变更预览
  previewDiff() {
    const previewDiffCallback = this.data.get('previewDiffCallback');
    if (previewDiffCallback) {
      previewDiffCallback();
    }
  }
}
